import { BrowserRouter, Routes, Route } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";

// Routes
import Home from "@/routes/Home";
import Features from "@/routes/Features";
import Login from "@/routes/Login";
import Signup from "@/routes/Signup";
import ForgotPassword from "@/routes/ForgotPassword";
import ResetPassword from "@/routes/ResetPassword";
import Settings from "@/routes/Settings";
import ApiTokens from "@/routes/ApiTokens";
import Beta from "@/routes/Beta";
import Jams from "@/routes/Jams";
import JamDetail from "@/routes/JamDetail";
import JoinJam from "@/routes/JoinJam";
import Activate from "@/routes/Activate";
import AdminUsers from "@/routes/admin/Users";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireAdmin>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/features" element={<Features />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Protected routes with layout */}
            <Route
              path="/settings"
              element={
                <ProtectedLayout>
                  <Settings />
                </ProtectedLayout>
              }
            />
            <Route
              path="/api-tokens"
              element={
                <ProtectedLayout>
                  <ApiTokens />
                </ProtectedLayout>
              }
            />
            <Route
              path="/jams"
              element={
                <ProtectedLayout>
                  <Jams />
                </ProtectedLayout>
              }
            />
            <Route
              path="/jams/:id"
              element={
                <ProtectedLayout>
                  <JamDetail />
                </ProtectedLayout>
              }
            />

            <Route
              path="/join/:code"
              element={
                <ProtectedLayout>
                  <JoinJam />
                </ProtectedLayout>
              }
            />

            <Route
              path="/activate"
              element={
                <ProtectedLayout>
                  <Activate />
                </ProtectedLayout>
              }
            />

            <Route
              path="/beta"
              element={
                <ProtectedLayout>
                  <Beta />
                </ProtectedLayout>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
